import {
  type ChangeEvent,
  type DragEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import { FileIcon, UploadIcon } from "./Icons";
import type {
  ReviewDocument,
  ReviewFileSource,
  ReviewTableCreateInput,
  ReviewTableSettings,
} from "./types";
import "./review.css";

const DEFAULT_SETTINGS: ReviewTableSettings = {
  instructions: "",
  language: "English (United Kingdom)",
  dateFormat: "Default — as written",
  currencyFormat: "Default — as written",
  numberFormat: "Default — as written",
};

const ACCEPTED_FILES = ".docx,.pdf,.xlsx,.pptx";

export interface ReviewTableSetupProps {
  initialName?: string;
  documents?: ReviewDocument[];
  settings?: Partial<ReviewTableSettings>;
  maxDocuments?: number;
  busy?: boolean;
  onFilesSelected?: (
    files: File[],
    source: Extract<ReviewFileSource, "computer" | "folder">
  ) => void | Promise<void>;
  onRequestSource?: (
    source: Extract<ReviewFileSource, "repository" | "project">
  ) => void;
  onSettingsChange?: (settings: ReviewTableSettings) => void;
  onCreateTable?: (
    input: ReviewTableCreateInput
  ) => void | Promise<void>;
  onCancel?: () => void;
}

export function ReviewTableSetup({
  initialName = "",
  documents = [],
  settings,
  maxDocuments = 50,
  busy = false,
  onFilesSelected,
  onRequestSource,
  onSettingsChange,
  onCreateTable,
  onCancel,
}: ReviewTableSetupProps) {
  const [name, setName] = useState(initialName);
  const [localSettings, setLocalSettings] = useState<ReviewTableSettings>({
    ...DEFAULT_SETTINGS,
    ...settings,
  });
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const selectionLabel = useMemo(
    () => `${documents.length} document${documents.length === 1 ? "" : "s"} selected`,
    [documents.length]
  );

  function updateSetting<K extends keyof ReviewTableSettings>(
    key: K,
    value: ReviewTableSettings[K]
  ) {
    const next = { ...localSettings, [key]: value };
    setLocalSettings(next);
    onSettingsChange?.(next);
  }

  function receiveFiles(
    event: ChangeEvent<HTMLInputElement>,
    source: "computer" | "folder"
  ) {
    const files = Array.from(event.target.files || []).slice(
      0,
      Math.max(maxDocuments - documents.length, 0)
    );
    if (files.length) void onFilesSelected?.(files, source);
    event.target.value = "";
  }

  function handleDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    if (!onFilesSelected || busy) return;
    const files = Array.from(event.dataTransfer.files).slice(
      0,
      Math.max(maxDocuments - documents.length, 0)
    );
    if (files.length) void onFilesSelected(files, "computer");
  }

  function browseFolder() {
    if (!onFilesSelected || busy) return;
    folderInput.current?.setAttribute("webkitdirectory", "");
    folderInput.current?.click();
  }

  const createDisabled = busy || documents.length === 0 || !onCreateTable;

  return (
    <section className="di-review di-review-setup" aria-label="New review table">
      <div className="di-review-setup__main">
        <div className="di-review-setup__form">
          <label className="di-field">
            <span>Name this review table</span>
            <input
              className="di-input"
              disabled={busy}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Change-of-control sweep — Larkspur"
              type="text"
              value={name}
            />
          </label>

          <div className="di-section-heading">
            <h2>Documents</h2>
            <p>
              Each document becomes a row. Add them now — columns are defined
              once DI can see what it is reading.
            </p>
          </div>

          <div
            aria-disabled={!onFilesSelected || busy}
            className={`di-drop-zone${dragging ? " is-dragging" : ""}${
              !onFilesSelected ? " is-disabled" : ""
            }`}
            onDragEnter={(event) => {
              event.preventDefault();
              if (onFilesSelected && !busy) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
          >
            <span className="di-accent-icon"><UploadIcon size={24} /></span>
            <strong>Drag files or a folder here</strong>
            <span>.docx, .pdf, .xlsx, .pptx · up to {maxDocuments} documents per table</span>
          </div>

          <input
            accept={ACCEPTED_FILES}
            className="di-visually-hidden"
            multiple
            onChange={(event) => receiveFiles(event, "computer")}
            ref={fileInput}
            tabIndex={-1}
            type="file"
          />
          <input
            accept={ACCEPTED_FILES}
            className="di-visually-hidden"
            multiple
            onChange={(event) => receiveFiles(event, "folder")}
            ref={folderInput}
            tabIndex={-1}
            type="file"
          />

          <div className="di-button-row">
            <button
              className="di-btn di-btn--secondary"
              disabled={!onFilesSelected || busy}
              onClick={() => fileInput.current?.click()}
              title={!onFilesSelected ? "File upload is not connected." : undefined}
              type="button"
            >
              Browse files
            </button>
            <button
              className="di-btn di-btn--secondary"
              disabled={!onFilesSelected || busy}
              onClick={browseFolder}
              title={!onFilesSelected ? "Folder upload is not connected." : undefined}
              type="button"
            >
              Browse a folder
            </button>
            <button
              className="di-btn di-btn--secondary"
              disabled={!onRequestSource || busy}
              onClick={() => onRequestSource?.("repository")}
              title={!onRequestSource ? "Repository selection is not connected." : undefined}
              type="button"
            >
              Add from Contract Repository
            </button>
            <button
              className="di-btn di-btn--secondary"
              disabled={!onRequestSource || busy}
              onClick={() => onRequestSource?.("project")}
              title={!onRequestSource ? "Project selection is not connected." : undefined}
              type="button"
            >
              Add from a project
            </button>
          </div>

          {documents.length > 0 && (
            <div className="di-document-list" aria-label="Selected documents">
              {documents.map((document) => (
                <div className="di-document-list__row" key={document.id}>
                  <span className="di-accent-icon"><FileIcon /></span>
                  <span className="di-document-list__name">{document.name}</span>
                  {document.pages != null && (
                    <span className="di-muted">{document.pages} pp.</span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="di-setup-actions">
            <span className="di-muted">{selectionLabel} — you can add more later.</span>
            <span className="di-spacer" />
            <button
              className="di-btn di-btn--secondary"
              disabled={!onCancel || busy}
              onClick={onCancel}
              title={!onCancel ? "Cancel navigation is not connected." : undefined}
              type="button"
            >
              Cancel
            </button>
            <button
              className="di-btn di-btn--primary"
              disabled={createDisabled}
              onClick={() => void onCreateTable?.({
                name: name.trim() || "Untitled review table",
                documentIds: documents.map((document) => document.id),
                settings: localSettings,
              })}
              title={!onCreateTable ? "Table creation is not connected." : undefined}
              type="button"
            >
              {busy ? "Creating…" : "Create table"}
            </button>
          </div>
        </div>
      </div>

      <aside className="di-review-setup__aside" aria-label="Table settings">
        <div className="di-section-heading">
          <h2>Table settings</h2>
          <p>Set these once and every column prompt in the table inherits them.</p>
        </div>
        <label className="di-field">
          <span>Instructions</span>
          <textarea
            className="di-input di-textarea"
            maxLength={10000}
            onChange={(event) => updateSetting("instructions", event.target.value)}
            placeholder="Refer to the parties by their role rather than their entity names. Quote the clause where the answer is a term of art."
            value={localSettings.instructions}
          />
          <small>{localSettings.instructions.length.toLocaleString()} / 10,000</small>
        </label>
        <SettingsSelect
          label="Language"
          onChange={(value) => updateSetting("language", value)}
          options={[
            "English (United Kingdom)",
            "English (United States)",
            "German",
            "French",
          ]}
          value={localSettings.language}
        />
        <SettingsSelect
          label="Date format"
          onChange={(value) => updateSetting("dateFormat", value)}
          options={["Default — as written", "31 December 2026", "2026-12-31"]}
          value={localSettings.dateFormat}
        />
        <SettingsSelect
          label="Currency"
          onChange={(value) => updateSetting("currencyFormat", value)}
          options={["Default — as written", "EUR · €1,250,000", "GBP · £1,250,000"]}
          value={localSettings.currencyFormat}
        />
        <SettingsSelect
          label="Numbers"
          onChange={(value) => updateSetting("numberFormat", value)}
          options={["Default — as written", "1,250.00", "1.250,00"]}
          value={localSettings.numberFormat}
        />
        <div className="di-callout">
          <span>Columns</span>
          <p>
            Available once the documents are added — the Column builder,
            filters and the Ask DI panel appear here.
          </p>
        </div>
      </aside>
    </section>
  );
}

function SettingsSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="di-field">
      <span>{label}</span>
      <select
        className="di-input"
        onChange={(event) => onChange(event.target.value)}
        value={value}
      >
        {options.map((option) => (
          <option key={option}>{option}</option>
        ))}
      </select>
    </label>
  );
}
